import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, CheckCircle2, ExternalLink } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { adminInput } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DESTS = [
  ["access_request", "Convert to Access Request"],
  ["invitation_draft", "Create Invitation Draft"],
  ["reservation_draft", "Create Reservation Draft"],
  ["event_inquiry", "Create Event Inquiry"],
  ["membership_followup", "Create Membership Follow-Up"],
  ["followup_task", "Create Follow-Up Task"],
];
const TYPE_DEFAULT = { invitation: "invitation_draft", reservation: "reservation_draft", event: "event_inquiry", membership: "membership_followup" };
const Row = ({ label, value }) => value ? (
  <div className="flex gap-2 text-sm py-0.5"><span className="text-muted-foreground w-36 shrink-0">{label}</span><span className="text-foreground break-words">{String(value)}</span></div>
) : null;

export const LeadConvertModal = ({ lead, onClose, onDone }) => {
  const nav = useNavigate();
  const [dest, setDest] = useState("followup_task");
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  const [dupe, setDupe] = useState(null);
  const [done, setDone] = useState(null);
  React.useEffect(() => {
    if (lead) {
      setDest(TYPE_DEFAULT[lead.type] || "followup_task");
      setFields({ name: lead.name || "", email: lead.email || "", phone: lead.phone || "",
        preferred_dates: lead.preferred_dates || "", experience: lead.experience || "",
        celebration: lead.celebration || "", custom_message: "" });
      setDupe(null); setDone(null);
    }
  }, [lead]);
  if (!lead) return null;
  const setF = (k, v) => setFields((p) => ({ ...p, [k]: v }));

  const submit = async (override) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/convert`, { destination: dest, fields, override_duplicate: !!override });
      if (data.duplicate) { setDupe(data); }
      else { setDone(data); toast.success(`${data.link.label} created.`); onDone && onDone(); }
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-surface border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-convert-modal">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Review &amp; Convert Lead</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-3" data-testid="lead-convert-done">
            <p className="text-emerald-300 flex items-center gap-2"><CheckCircle2 size={18} /> {done.link.label} created as a draft — no approval, invitation, booking, or communication was sent.</p>
            <div className="flex gap-2">
              <button onClick={() => { nav(done.link.route); onClose(); }} className="btn-gold px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="lead-open-record"><ExternalLink size={14} /> Open {done.link.label}</button>
              <button onClick={onClose} className="btn-ghost-gold px-4 py-2 text-sm">Close</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-white/10 p-3">
              <p className="text-xs uppercase tracking-label text-gold mb-1.5">Lead detail</p>
              <Row label="Name" value={lead.name} />
              <Row label="Email" value={lead.email} />
              <Row label="Telephone" value={lead.phone} />
              <Row label="Inquiry type" value={lead.type} />
              <Row label="Area of interest" value={lead.interest} />
              <Row label="Preferred dates" value={lead.preferred_dates} />
              <Row label="Guests" value={[lead.adults, lead.children].some((x) => x != null) ? `${lead.adults || 0} adults · ${lead.children || 0} children` : ""} />
              <Row label="Celebration" value={lead.celebration} />
              <Row label="Why The D?" value={lead.why_join} />
              <Row label="Preferred contact" value={lead.contact_method} />
              <Row label="Consent" value={lead.consent ? "Given" : "Not given"} />
              <Row label="Source page" value={lead.source} />
              <Row label="D conversation" value={lead.ghost_conversation_id} />
              {(lead.converted_to || []).length > 0 && <Row label="Existing records" value={(lead.converted_to || []).map((c) => c.label).join(", ")} />}
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Conversion destination</label>
              <select className={adminInput} value={dest} onChange={(e) => { setDest(e.target.value); setDupe(null); }} data-testid="lead-dest-select">
                {DESTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input className={adminInput} placeholder="Name" value={fields.name} onChange={(e) => setF("name", e.target.value)} data-testid="lead-field-name" />
              <input className={adminInput} placeholder="Email" value={fields.email} onChange={(e) => setF("email", e.target.value)} data-testid="lead-field-email" />
              <input className={adminInput} placeholder="Telephone" value={fields.phone} onChange={(e) => setF("phone", e.target.value)} />
              {(dest === "reservation_draft") && <input className={adminInput} placeholder="Preferred dates" value={fields.preferred_dates} onChange={(e) => setF("preferred_dates", e.target.value)} />}
              {(dest === "reservation_draft") && <input className={adminInput} placeholder="Experience" value={fields.experience} onChange={(e) => setF("experience", e.target.value)} />}
              {(dest === "event_inquiry") && <input className={adminInput} placeholder="Celebration / event type" value={fields.celebration} onChange={(e) => setF("celebration", e.target.value)} />}
              {(dest === "invitation_draft") && <input className={adminInput + " col-span-2"} placeholder="Custom invitation message" value={fields.custom_message} onChange={(e) => setF("custom_message", e.target.value)} />}
            </div>

            {dupe && (
              <div className="rounded-lg border border-amber-400/40 bg-amber-400/5 p-3" data-testid="lead-duplicate-warning">
                <p className="text-sm text-amber-300 flex items-center gap-2"><AlertTriangle size={15} /> A {dupe.label} was already created from this lead{dupe.existing?.at ? ` on ${dupe.existing.at.slice(0, 10)}` : ""}.</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => submit(true)} disabled={busy} className="btn-gold px-4 py-2 text-xs" data-testid="lead-convert-anyway">Convert anyway</button>
                  <button onClick={() => setDupe(null)} className="btn-ghost-gold px-4 py-2 text-xs">Cancel</button>
                </div>
              </div>
            )}

            {!dupe && (
              <div className="flex gap-2">
                <button onClick={() => submit(false)} disabled={busy} className="btn-gold px-5 py-2.5 text-sm inline-flex items-center gap-1" data-testid="lead-convert-confirm">Confirm &amp; Convert <ArrowRight size={14} /></button>
                <button onClick={onClose} className="btn-ghost-gold px-5 py-2.5 text-sm">Cancel</button>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">Conversion creates a draft only. It never approves access, sends an invitation, confirms a reservation, approves an event or membership, or sends any communication. The original lead is preserved and linked.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
